import { useRef, useState } from "react";
import { FiCheck, FiGrid, FiList } from "react-icons/fi";
import type {
  CatalogEvent,
  CatalogEventNotifier,
} from "@/features/catalog/mediators/catalogEvents";
import { isCatalogEvent } from "@/features/catalog/mediators/catalogEvents";
import {
  type CatalogDisplayType,
  type CatalogSortModalEffect,
  createCatalogSortModalMediator,
  isCatalogSortModalEvent,
} from "@/features/catalog/mediators/catalogSortModalMediator";
import type { SortType } from "@/features/catalog/stores/catalogStore";
import { CATALOG_SORT_OPTIONS } from "@/features/catalog/utils/catalogSortOptions";
import { selectDesktopSort } from "@/features/catalog/utils/desktopCatalogSort";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { Modal } from "@/shared/ui/overlay";

interface CatalogSortModalProps {
  isOpen: boolean;
  initialDisplayType?: CatalogDisplayType;
  currentSort: SortType;
  selectSortMethod: (sort: SortType) => void;
  onShowCatalog: () => void;
  onShowList: () => void;
  onClose: () => void;
}

export function CatalogSortModal({
  isOpen,
  initialDisplayType = "catalog",
  currentSort,
  selectSortMethod,
  onShowCatalog,
  onShowList,
  onClose,
}: CatalogSortModalProps): React.ReactElement {
  const effectRunner = useRef<(effect: CatalogSortModalEffect) => void>(
    (): void => undefined,
  );
  effectRunner.current = (effect: CatalogSortModalEffect): void => {
    if (effect.type === "close") {
      onClose();
      return;
    }
    selectDesktopSort(currentSort, effect.sort, selectSortMethod);
    onClose();
    if (effect.displayType === "catalog") onShowCatalog();
    else onShowList();
  };
  const [mediator] = useState(() =>
    createCatalogSortModalMediator(initialDisplayType, (effect) =>
      effectRunner.current(effect),
    ),
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (!isCatalogEvent(event) || !isCatalogSortModalEvent(event)) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const state = useMediatorSnapshot(mediator);
  const notify: CatalogEventNotifier = (
    event: CatalogEvent,
  ): ReturnType<CatalogEventNotifier> => node.dispatch(event).status;

  return (
    <ComponentScope node={node}>
      <CatalogSortModalView
        isOpen={isOpen}
        displayType={state.displayType}
        selectedSort={currentSort}
        onEvent={notify}
      />
    </ComponentScope>
  );
}

export type CatalogSortModalViewProps = {
  readonly isOpen: boolean;
  readonly displayType: CatalogDisplayType;
  readonly selectedSort: SortType;
  readonly onEvent: CatalogEventNotifier;
};

export function CatalogSortModalView({
  isOpen,
  displayType,
  selectedSort,
  onEvent,
}: CatalogSortModalViewProps): React.ReactElement {
  return (
    <Modal
      isOpen={isOpen}
      onClose={(): void => {
        onEvent({ type: "catalog/sort-modal-close-requested" });
      }}
      title="カタログソート"
      position="bottom"
      flickToClose={false}
    >
      <section className="space-y-4 p-4" aria-label="カタログ表示設定">
        <fieldset className="m-0 border-0 p-0">
          <legend className="mb-2 text-sm font-semibold text-foreground">
            表示タイプ
          </legend>
          <fieldset
            aria-label="表示タイプ"
            className="history-mode-toggle catalog-sort-display-toggle max-w-none"
          >
            <button
              type="button"
              aria-pressed={displayType === "catalog"}
              className="inline-flex items-center justify-center gap-2"
              onClick={(): void => {
                onEvent({
                  type: "catalog/sort-modal-display-changed",
                  displayType: "catalog",
                });
              }}
            >
              <FiGrid aria-hidden="true" />
              カタログ
            </button>
            <button
              type="button"
              aria-pressed={displayType === "list"}
              className="inline-flex items-center justify-center gap-2"
              onClick={(): void => {
                onEvent({
                  type: "catalog/sort-modal-display-changed",
                  displayType: "list",
                });
              }}
            >
              <FiList aria-hidden="true" />
              リスト
            </button>
          </fieldset>
        </fieldset>

        <fieldset className="m-0 border-0 p-0">
          <legend className="mb-2 text-sm font-semibold text-foreground">
            並び順
          </legend>
          <ul className="m-0 list-none divide-y divide-border border-y border-border p-0">
            {CATALOG_SORT_OPTIONS.map(({ label, sort }) => (
              <li key={sort}>
                <button
                  type="button"
                  aria-pressed={selectedSort === sort}
                  className="flex min-h-12 w-full items-center justify-between px-3 py-2 text-left font-medium text-foreground hover:bg-muted"
                  onClick={(): void => {
                    onEvent({
                      type: "catalog/sort-modal-sort-selected",
                      sort,
                    });
                  }}
                >
                  {label}
                  {selectedSort === sort && (
                    <FiCheck aria-hidden="true" className="text-primary" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </fieldset>
      </section>
    </Modal>
  );
}
